import ContentSectionsNew from 'components/ContentSectionsNew';
import { useGetLandingJoinContentPageQuery } from '../../redux/api/content.api';
import { useParams } from 'react-router-dom';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addPopup } from 'redux/actions';
import * as popups from 'constants/popup-types';
import { SendPlatform, ShareAnalyticsMethod } from 'app/enums';
import { generateTestId, getMediaPath } from 'app/functions';
import ShareButton from './components/ShareButton';
import FixedShareButton from './components/FixedShareButton';
import AnalyticsManager from 'app/analytics';
import { SHARE_CONTENT_SOURCE } from 'pages/personal-zone/utils/enums';
import {    
    shareViaEmail,
    shareViaSMS,
    shareViaWhatsApp,
    shareViaNativeShare,
    extractEmailTitle,
    copyToClipboard,
} from './utils/utils';
import { getCopyButtonTooltipProps } from './utils/tooltipProps';
import HotTypography from 'containers/popups/custom/ShareLinkPopup/HotTypography';
import { isMobileDevice } from 'common/utils';
import Loader from 'components/common/loader';
import Page404 from 'containers/page404';

const LandingJoinContentPage = ({ testID = 'landing-join-content-page' }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { name } = useParams();
    const translations = useSelector((store) => store.config.Translations);
    const user = useSelector((store) => store.config.UserData);
    const { data: pageData, isLoading, isError } = useGetLandingJoinContentPageQuery({ slug: name });
    const [sections, setSections] = useState(pageData?.contentSections);
    const [popupContent, setPopupContent] = useState(null);
    const [canShare, setCanShare] = useState(true);
    const [toastOpen, setToastOpen] = useState(false);
    const [toastOpenFixed, setToastOpenFixed] = useState(false);
    const [platform, setPlatform] = useState(null);

    useEffect(() => {
        if (pageData) {
            setSections(pageData?.contentSections);
            setPopupContent(pageData?.popupContent);
        }
    }, [pageData]);

    useEffect(() => {
        if (navigator.share) {
            setCanShare(true);
        } else {
            setCanShare(false);
        }
    }, []);


    useEffect(() => {
       if (platform && user)  {
        handleShare();
       }
    }, [user, platform]);


    const handleLogin = () => {
        dispatch(
            addPopup({
                type: popups.LOGIN,
                subtitleText: translations?.login_share_title,
               
            })
        );
    };

    const requireLogin = () => {
        if (!user) {
            handleLogin();
            return false;
        }
        return true;
    };

    const getShareUrl = (sendPlatform) => {
        let baseUrlLink = popupContent?.baseUrlLink || '';
        if (user?.uniqueId) {
            baseUrlLink = baseUrlLink + `?m=${user.uniqueId}`;
        }
        return baseUrlLink + `&c=${Math.floor(new Date().getTime()/60000)}&p=${sendPlatform}`;
    };

    const logShare = (method) => {
        AnalyticsManager.logShareUrl(SHARE_CONTENT_SOURCE.JOIN_PAGE, method, popupContent?.baseUrlLink);
    }

    const handleShare = async () => {
        if (!platform) return;
        const formattedMessageContent = formatMessageContent(pageData?.messageContent, getShareUrl(platform));
        switch (platform) {
            case SendPlatform.WHATSAPP:
                setToastOpen(false);
                logShare(ShareAnalyticsMethod.WHATSAPP);
                shareViaWhatsApp(formattedMessageContent);
                setPlatform(null);
                break;
            case SendPlatform.EMAIL:
                setToastOpen(false);
                logShare(ShareAnalyticsMethod.EMAIL);
                shareViaEmail(formattedMessageContent, extractEmailTitle(popupContent));
                setPlatform(null);
                break;
            case SendPlatform.SMS:
                setToastOpen(false);
                logShare(ShareAnalyticsMethod.SMS);
                shareViaSMS(formattedMessageContent);
                setPlatform(null);
                break;
            case SendPlatform.COPY_LINK:
                logShare(ShareAnalyticsMethod.COPY_LINK);
                const success = await copyToClipboard(formattedMessageContent);
                if (success) {
                    setToastOpen(true);
                    setToastOpenFixed(true);
                    setPlatform(null);
                    setTimeout(() => {
                        setToastOpen(false);
                        setToastOpenFixed(false);
                    }, 2000);
                }
                break;
            case SendPlatform.SHARE:
                setToastOpen(false);
                logShare(ShareAnalyticsMethod.SHARE);
                const shared = await shareViaNativeShare(getShareUrl(platform), formattedMessageContent, popupContent?.emailSubject);
                if (!shared) {
                    logShare(ShareAnalyticsMethod.COPY_LINK);
                    await copyToClipboard(getShareUrl(platform));
                }
                setPlatform(null);
                break;
        }
    };

    const handleShareButtonClick = async (platform) => {
        setPlatform(platform);
        if (!requireLogin()) return;
    }

    const formatMessageContent = (messageContent, url) => {
        if (!messageContent) return '';
        let formatted = messageContent;

        if (formatted.includes('{{url}}')) {
            formatted = formatted.replace('{{url}}', url || '');
        }
        if (formatted.includes('{{user_fullname}}')) {
            const fullName = user?.first_name + ' ' + user?.last_name;
            formatted = formatted.replace('{{user_fullname}}', fullName);
        }
        return formatted;
    };

    const buttonGap = isMobile ? 2.5 : 0;
    const containerGap = canShare ? (isMobile ? 2.5 : 2.5) : buttonGap;

    const whatsAppImage = popupContent?.whatsAppImage ? getMediaPath(popupContent.whatsAppImage) : null;
    const smsImage = popupContent?.smsImage ? getMediaPath(popupContent.smsImage) : null;
    const emailImage = popupContent?.emailImage ? getMediaPath(popupContent.emailImage) : null;
    const copyImage = popupContent?.copyImage ? getMediaPath(popupContent.copyImage) : null;
    const shareImage = popupContent?.shareImage ? getMediaPath(popupContent.shareImage) : null;

    if (isLoading) return <Loader className="initial-loader" />;
    if (isError || !pageData) return <Page404 />;

    return (
        <div testID={generateTestId(testID, 'page-wrapper')} style={{ position: 'relative' }}>
            <Box testID={generateTestId(testID, 'content-wrapper')} 
                sx={{ 
                    backgroundColor: theme.palette.background.default,
                    [theme.breakpoints.up('md')]: { backgroundColor: theme.palette.background.paper }
                }}>
                {sections && sections.length > 0 && <ContentSectionsNew section={sections[0]} backgroundColor={theme.palette.background.default} fontWeight={700} />}
                {popupContent && !isMobileDevice() && <Box
                    testID={generateTestId(testID, 'share-section')}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                        margin: '0 auto',
                        gap: 1.5,
                        padding: isMobile ? '10px' : '30px',
                        paddingBottom: isMobile ? '20px' : '30px',
                        paddingTop: isMobile ? '20px' : '30px',
                    }}
                >
                    <HotTypography
                        text={popupContent?.title || 'העתק קישור'}
                        size={isMobile ? '24px' : '30px'}
                        color={theme.palette.primary?.blue}
                        fontFamily={theme.typography.fontFamilyBold}
                        sx={{ display: 'block', visibility: 'visible' }}
                    />

                    <Box
                        testID={generateTestId(testID, 'share-buttons-container')}
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            width: canShare ? 'fit-content' : '100%',
                            gap: containerGap,
                            justifyContent: 'center',
                            alignItems: 'stretch',
                            maxWidth: isMobile ? '60%' : '400px',
                        }}
                    >
                        <ShareButton
                            type="copy"
                            iconImage={copyImage}
                            onClick={() => handleShareButtonClick(SendPlatform.COPY_LINK)}
                            theme={theme}
                            color={theme.palette.primary?.main}
                            label={popupContent?.copyLabel || 'העתק קישור'}
                            tooltipContent={translations?.link_copy_success || 'הקישור הועתק בהצלחה'}
                            tooltipOpen={toastOpen}
                            onTooltipClose={() => setToastOpen(false)}
                            tooltipPlacement="bottom"
                            canShare={canShare}
                            tooltipProps={getCopyButtonTooltipProps(theme, isMobile)}
                            testID={generateTestId(testID, 'copy-button')}
                        />
                        {canShare && (
                            <ShareButton
                                type="share"
                                iconImage={shareImage}
                                onClick={() => handleShareButtonClick(SendPlatform.SHARE)}
                                theme={theme}
                                label={popupContent?.shareLabel || 'שתף'}
                                color={theme.palette?.primary?.blue}
                                canShare={canShare}
                                testID={generateTestId(testID, 'share-button')}
                            />
                        )}
                        {!canShare && (<>
                            <ShareButton
                                type="whatsapp"
                                iconImage={whatsAppImage}
                                onClick={() => handleShareButtonClick(SendPlatform.WHATSAPP)}
                                theme={theme}
                                label={popupContent?.whatsappLabel || 'וואטסאפ'}
                                color={theme.palette.primary?.main}
                                canShare={canShare}
                                testID={generateTestId(testID, 'whatsapp-button')}
                            />
                            <ShareButton
                                type="email"
                                iconImage={emailImage}
                                onClick={() => handleShareButtonClick(SendPlatform.EMAIL)}
                                theme={theme}
                                label={popupContent?.emailLabel || 'אימייל'}
                                color={theme.palette.primary?.main}
                                canShare={canShare}
                                testID={generateTestId(testID, 'email-button')}
                            />
                            <ShareButton
                                type="sms"
                                iconImage={smsImage}
                                onClick={() => handleShareButtonClick(SendPlatform.SMS)}
                                theme={theme}
                                label={popupContent?.smsLabel || 'SMS'}
                                color={theme.palette.primary?.main}
                                canShare={canShare}
                                testID={generateTestId(testID, 'sms-button')}
                            />
                        </>)}
                    </Box>
                </Box>}

                {sections && sections.length > 0 && <ContentSectionsNew section={sections[1]} backgroundColor={theme.palette.background.default} fontWeight={700} />}
            </Box>
            {popupContent && (
                <FixedShareButton
                    canShare={canShare}
                    isMobile={isMobile}
                    theme={theme}
                    popupContent={popupContent}
                    whatsAppImage={whatsAppImage}
                    smsImage={smsImage}
                    emailImage={emailImage}
                    copyImage={copyImage}
                    shareImage={shareImage}
                    translations={translations}
                    handleCopyLink={() => handleShareButtonClick(SendPlatform.COPY_LINK)}
                    handleShareClick={() => handleShareButtonClick(SendPlatform.SHARE)}
                    handleWhatsappShareClick={() => handleShareButtonClick(SendPlatform.WHATSAPP)}
                    handleEmailShareClick={() => handleShareButtonClick(SendPlatform.EMAIL)}
                    handleSmsShareClick={() => handleShareButtonClick(SendPlatform.SMS)}
                    toastOpen={toastOpenFixed}
                    setToastOpen={setToastOpenFixed}
                    getCopyButtonTooltipProps={getCopyButtonTooltipProps}
                    testID={generateTestId(testID, 'fixed-share-button')}
                />
            )}
        </div>
    );
};

export default LandingJoinContentPage;
